import { useTranslation } from "react-i18next";
import { DateRangeType } from "react-tailwindcss-datepicker";

import CustomDatePicker from "../awaitingInventory/CustomDatePicker";
import LoadingWheel from "../shared/LoadingWheel";
import {
  IStationCycleTime,
  PartAssemblyType,
  ProductionDashboardAssemblyKind,
} from "../../models/ProductionDashboard";
import DashboardTile from "./DashboardTile";

interface IStationCycleTimeTableProps {
  stations: IStationCycleTime[] | undefined;
  isLoading: boolean;
  dates: DateRangeType;
  onDatesChange: (dates: DateRangeType) => void;
  span?: 1 | 2;
}

/** Seconds as "7m 30s", or "1h 04m" once it runs past the hour. */
function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;

  const totalMinutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (totalMinutes < 60) {
    return remainingSeconds === 0 ? `${totalMinutes}m` : `${totalMinutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

/**
 * Colour is only applied where there is something to compare against. A station with no target
 * shows its median in plain text rather than green — "no target set" is not the same as "on time",
 * and colouring it would invent a judgement nobody configured.
 */
function comparisonClass(median: number | null, target: number | null): string {
  if (median == null || target == null) return "text-gray-900";
  if (median > target) return "text-rose-700";
  return "text-emerald-700";
}

export default function StationCycleTimeTable({
  stations,
  isLoading,
  dates,
  onDatesChange,
  span = 2,
}: IStationCycleTimeTableProps) {
  const { t } = useTranslation("dashboard");

  // Order comes from the server, which merges Triage and Depollution into the configured flow when
  // "Include Triage/Depollution" is on and puts them after the assemblies when it is off. Sorting
  // on it is all this table has to do - it never decides where the two stages belong.
  const rows = (stations ?? []).slice().sort((a, b) => a.order - b.order);
  const measured = rows.filter((s) => s.medianSeconds != null).length;

  /** Stages are labelled from their kind so they translate; assemblies keep their entered name. */
  const stationLabel = (station: IStationCycleTime) => {
    if (station.kind === ProductionDashboardAssemblyKind.Triage) return t("Triage");
    if (station.kind === ProductionDashboardAssemblyKind.Depollution) return t("Depollution");
    return station.name;
  };

  // The picker's clear button hands back {startDate: null, endDate: null}, which disables the
  // query. Without this the card would spin forever: a disabled react-query v4 query that has
  // never fetched reports isLoading: true indefinitely, so "no range" looked like "still loading".
  const hasRange = !!dates.startDate && !!dates.endDate;

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="prose font-semibold text-gray-900">{t("Cycle Time by Station")}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {t("Median time a vehicle takes through each station, excluding paused time")}
            </p>
          </div>
          <CustomDatePicker
            dates={dates}
            handleDatesChange={onDatesChange}
            inputClassName="h-10 w-full text-sm border-gray-300 rounded-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            containerClassName="relative w-64 shrink-0 text-gray-700"
          />
        </div>

        {!hasRange ? (
          <p className="py-6 text-center text-sm text-gray-500">
            {t("Choose a date range to see cycle times.")}
          </p>
        ) : isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingWheel />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">{t("No stations configured.")}</p>
        ) : (
          <>
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200">
                    <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">{t("Station")}</th>
                    <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Vehicles")}</th>
                    <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Median")}</th>
                    <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Target")}</th>
                    <th
                      className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800"
                      title={t("Median wait after this station finished before the next one started")}
                    >
                      {t("Idle")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((station) => {
                    const isStage = station.kind !== ProductionDashboardAssemblyKind.Assembly;

                    return (
                      <tr
                        key={station.partAssemblyId ?? `stage-${station.kind}`}
                        className={`border-b border-gray-100 last:border-0 ${isStage ? "bg-gray-50" : ""}`}
                      >
                        <td className="py-2.5 pl-4 pr-4 font-medium text-gray-900">
                          {stationLabel(station)}
                          {isStage ? (
                            <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                              {t("Stage")}
                            </span>
                          ) : (
                            station.type === PartAssemblyType.Sub && (
                              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                {t("Sub")}
                              </span>
                            )
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-right text-gray-600">{station.vehicleCount}</td>
                        <td
                          className={`py-2.5 pr-4 text-right font-bold ${comparisonClass(
                            station.medianSeconds,
                            station.targetSeconds
                          )}`}
                        >
                          {formatDuration(station.medianSeconds)}
                        </td>
                        <td className="py-2.5 pr-4 text-right text-gray-500">
                          {formatDuration(station.targetSeconds)}
                        </td>
                        {/* N/A and — are different facts: nothing follows this row to wait for, vs
                            no wait could be measured in the period. */}
                        <td className="py-2.5 pr-4 text-right text-gray-600">
                          {station.measuresIdle ? formatDuration(station.medianIdleSeconds) : t("N/A")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Sample size is part of the number: a median over two vehicles is not a line speed. */}
            <p className="mt-2 text-xs text-gray-500">
              {measured === 0
                ? t("No vehicles completed a station in this period.")
                : t("{{measured}} of {{total}} stations had vehicles through in this period. A median over very few vehicles is not a reliable line speed.", {
                    measured,
                    total: rows.length,
                  })}
            </p>
          </>
        )}
      </div>
    </DashboardTile>
  );
}
